import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/Urls";

import catElectronics from "../assets/cat-electronics.jpg";
import catFurniture from "../assets/cat-furniture.jpg";
import catClothes from "../assets/cat-clothes.jpg";
import catBooks from "../assets/cat-books.jpg";
import catAppliances from "../assets/cat-appliances.jpg";
import catVehicles from "../assets/cat-vehicles.jpg";
import catTools from "../assets/cat-tools.jpg";
import catSports from "../assets/cat-sports.jpg";
import catOthers from "../assets/cat-others.jpg";

const CATS_CACHE_KEY = "cats";
const COUNTS_CACHE_KEY = "category_item_counts";

const CategoryGrid = () => {
  const [categories, setCategories] = useState([]);
  const [counts, setCounts] = useState({});

  const imageMap = {
    Electronics: catElectronics,
    Furniture: catFurniture,
    Clothes: catClothes,
    Books: catBooks,
    "Home Appliances": catAppliances,
    Vehicles: catVehicles,
    Tools: catTools,
    "Sports & Fitness": catSports,
    Others: catOthers,
  };

  useEffect(() => {
    loadCachedData();
    fetchFreshData();
  }, []);

  const loadCachedData = () => {
    const cachedCats = localStorage.getItem(CATS_CACHE_KEY);
    const cachedCounts = localStorage.getItem(COUNTS_CACHE_KEY);

    if (cachedCats) {
      try {
        const parsedCats = JSON.parse(cachedCats);

        if (Array.isArray(parsedCats)) {
          setCategories(parsedCats);
        }
      } catch (error) {
        console.error("Invalid cached categories:", error);
        localStorage.removeItem(CATS_CACHE_KEY);
      }
    }

    if (cachedCounts) {
      try {
        const parsedCounts = JSON.parse(cachedCounts);

        if (parsedCounts && typeof parsedCounts === "object") {
          setCounts(parsedCounts);
        }
      } catch (error) {
        console.error("Invalid cached counts:", error);
        localStorage.removeItem(COUNTS_CACHE_KEY);
      }
    }
  };

  const fetchFreshData = async () => {
    try {
      const [catRes, itemRes] = await Promise.all([
        api.get("/api/categories"),
        api.get("/api/category-items"),
      ]);

      const countMap = {};

      itemRes.data.forEach((item) => {
        const key = item.categoryName?.trim();

        if (key) {
          countMap[key] = (countMap[key] || 0) + 1;
        }
      });

      setCategories(catRes.data);
      setCounts(countMap);

      localStorage.setItem(CATS_CACHE_KEY, JSON.stringify(catRes.data));
      localStorage.setItem(COUNTS_CACHE_KEY, JSON.stringify(countMap));
    } catch (err) {
      console.error("Failed to fetch fresh category data:", err);
    }
  };

  return (
    <section id="categories" className="container mx-auto px-4 py-16 md:py-24">
      <div className="mb-10 text-center">
        <h2 className="font-heading text-3xl font-bold md:text-4xl">
          Browse by <span className = "text-primary" >Category</span>
        </h2>

        <p className="mt-3 text-muted-foreground">
          Find exactly what you need — or freely post what you no longer need.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {categories.map((cat, i) => (
          <Link
            key={cat.categoryId || cat.categoryName}
            to={`/category/${cat.categoryName}`}
            className="group relative overflow-hidden rounded-lg border border-border bg-card transition-all hover:border-primary/40 hover:shadow-[var(--glow-primary)] shadow-xl"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="relative h-48 overflow-hidden">
              <div className="absolute top-3 right-3 z-10 rounded-full bg-black/70 px-3 py-1 text-xs font-medium text-yellow-400 backdrop-blur border border-border">
                {counts[cat.categoryName?.trim()] || 0}+ Items
              </div>

              <img
                src={imageMap[cat.categoryName] || catOthers}
                alt={cat.categoryName}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
            </div>

            <div className="relative -mt-6 px-5 pb-5">
              <h3 className="font-heading text-lg font-semibold text-primary">
                {cat.categoryName}
              </h3>

              <p className="mt-1 text-sm text-muted-foreground">
                {cat.categoryDescription}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default CategoryGrid;